import React, { useEffect, useRef, useState, useCallback } from "react";
import dayjs from "dayjs";
import {
  getChatByOrderId,
  pushDriverMsg,
  driverAlreadyReadMsg,
  uploadFile,
  isOrderChatEnabled
} from "@/api/airportTransfer";
import { useParams } from "react-router-dom";
import { useTranslation } from '@/i18n';
import "./index.scss";

 // 时间显示逻辑
  function shouldShowTime(currentTime, prevTime) {
    return currentTime - prevTime > 5 * 60 * 1000;
  }
  function formatTime(timestamp) {
    const now = dayjs();
    const msgTime = dayjs(timestamp);
    if (now.isSame(msgTime, "day")) {
      return msgTime.format("HH:mm");
    } else if (now.subtract(1, "day").isSame(msgTime, "day")) {
      return `昨天 ${msgTime.format("HH:mm")}`;
    } else {
      return msgTime.format("MM-DD HH:mm");
    }
  }

// 消息处理函数，统一格式化消息
const parseMessages = (arr, prevMsgs = []) => {
  return arr.map((item, idx) => {
    let content = "";
    let type = "text";
    try {
      const msgContent = JSON.parse(item.msgContent);
      if (item.msgType === 1) {
        content = msgContent.text || "";
        type = "text";
      } else if (item.msgType === 4) {
        content = msgContent.url || "";
        type = "image";
      }
    } catch {
      content = item.msgContent;
      type = "text";
    }
    const messageTime = new Date(item.createTime).getTime();
    const prevTime =
      idx === 0
        ? prevMsgs.length > 0
          ? prevMsgs[prevMsgs.length - 1].time
          : 0
        : new Date(arr[idx - 1].createTime).getTime();
    return {
      id: item.id,
      content,
      type,
      time: messageTime,
      isSelf: item.msgSource === 2,
      status: "sent",
      showTime: idx === 0 || shouldShowTime(messageTime, prevTime),
      timeStr: formatTime(messageTime),
      msgId: item.id,
      appMsgId: item.appMsgId,
      driverName: item.driverName,
      readStatus: item.readStatus,
    };
  });
};

const ChatPage = () => {
  const { orderId } = useParams();
  const t = useTranslation();
  const [userInfo, setUserInfo] = useState({});
  const [passengerPhoneSuffix, setPassengerPhoneSuffix] = useState('');
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [lastMessageTime, setLastMessageTime] = useState(0);
  const [messageIdMap, setMessageIdMap] = useState({});
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");
  const [canChat, setCanChat] = useState(true); // 新增状态
  const [showTop, setShowTop] = useState(true); // 新增顶部显示状态
  const scrollBottomRef = useRef(null);
  const messageListRef = useRef(null); // 新增ref

  // 获取用户信息
  useEffect(() => {
    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
    setUserInfo(userInfo);
  }, []);

  // 判断是否能聊天
  useEffect(() => {
    (async () => {
      try {
        const res = await isOrderChatEnabled(orderId);
        // 兼容后端返回格式
        if (res.data?.errorCode === 0 && res.data?.data === true) {
          setCanChat(true);
        } else {
          setCanChat(false);
        }
      } catch {
        setCanChat(false);
      }
    })();
  }, [orderId]);

  // 初始化消息列表
  useEffect(() => {
    (async () => {
      setIsLoadingMore(true);
      try {
        const res = await getChatByOrderId(orderId);
        if (res.data.errorCode == 0 && res.data.data) {
          const arr = res.data.data.imMessageVoList || [];
          const passengerPhoneSuffix = res.data.data.passengerPhoneSuffix || "";
          document.title = t('chat.title', { passengerPhoneSuffix });
          setPassengerPhoneSuffix(passengerPhoneSuffix);
          const msgs = parseMessages(arr);
          setMessages(msgs);
          setHasMoreMessages(arr.length === pageSize);
          setLastMessageTime(msgs.length > 0 ? msgs[msgs.length - 1].time : 0);
        }
      } catch (e) {
        console.error("获取聊天记录失败", e);
      } finally {
        setIsLoadingMore(false);
      }
    })();
    // eslint-disable-next-line
  }, [orderId, page, pageSize]);

  // 滚动到底部，仅首次加载和当前在底部时
  useEffect(() => {
    if (!messageListRef.current || !scrollBottomRef.current) return;
    const list = messageListRef.current;
    // 判断是否在底部
    const isAtBottom =
      list.scrollHeight - list.scrollTop - list.clientHeight < 10;
    // 首次加载 或 当前在底部才滚动
    if (messages.length <= pageSize || isAtBottom) {
      scrollBottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, pageSize]);

  // 监听新消息（轮询）
  useEffect(() => {
    const timer = setInterval(async () => {
      try {
        const res = await getChatByOrderId(orderId);
        if (res.data.errorCode == 0 && res.data.data) {
          const arr = res.data.data.imMessageVoList || [];
          const latestMessages = parseMessages(arr);
          // 去重，只保留新消息
          const newMessages = latestMessages.filter(
            (msg) =>
              !messages.some(
                (m) =>
                  m.id === msg.id ||
                  (msg.appMsgId && m.appMsgId === msg.appMsgId) ||
                  (m.content === msg.content &&
                    Math.abs(m.time - msg.time) < 1000 &&
                    m.isSelf === msg.isSelf)
              ) &&
              msg.time > lastMessageTime
          );
          if (newMessages.length > 0) {
            setMessages((msgs) => [...msgs, ...newMessages]);
            setLastMessageTime(
              Math.max(
                lastMessageTime,
                ...newMessages.map((msg) => msg.time)
              )
            );
          }
        }
      } catch (e) {}
    }, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, [messages, lastMessageTime, messageIdMap, orderId]);

  //标记消息为已读
  useEffect(() => {
    (async () => {
      const unreadMsgIds = messages
        .filter((msg) => !msg.isSelf && msg.msgId && msg.readStatus === 0)
        .map((msg) => msg.msgId);
      if (unreadMsgIds.length > 0) {
        try {
          const res = await driverAlreadyReadMsg({ msgIds: unreadMsgIds,orderId });
          if (res.code === 10000) {
            setMessages((msgs) =>
              msgs.map((msg) =>
                unreadMsgIds.includes(msg.msgId)
                  ? { ...msg, readStatus: 1 }
                  : msg
              )
            );
          }
        } catch {}
      }
    })();
  }, [messages]);

  // 加载更多消息
  const loadMoreMessages = useCallback(async () => {
    if (!hasMoreMessages || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const res = await getChatByOrderId(orderId);
      if (res.data.errorCode == 0 && res.data.data) {
        const arr = res.data.data.imMessageVoList || [];
        const newMessages = parseMessages(arr, messages).reverse();
        // 去重
        const filtered = newMessages.filter(
          (msg) =>
            !messages.some(
              (m) =>
                m.id === msg.id ||
                (msg.appMsgId && msg.appMsgId === msg.appMsgId) ||
                (m.content === msg.content &&
                  Math.abs(m.time - msg.time) < 1000 &&
                  m.isSelf === msg.isSelf)
            )
        );
        setMessages((msgs) => [...filtered, ...msgs]);
        setPage((p) => p + 1);
        setHasMoreMessages(arr.length === pageSize);
      } else {
        setHasMoreMessages(false);
      }
    } catch {
      alert(t('chat.loadFailed'));
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMoreMessages, isLoadingMore, orderId, pageSize, messages]);

  // 输入框内容变化
  const onInputChange = (e) => setInputMessage(e.target.value);

  // 发送文本消息
  const sendMessage = async () => {
    if (!canChat) return; // 禁止发送
    if (!inputMessage.trim()) return;
    const messageContent = inputMessage;
    const appMsgId = `msg_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    const currentTime = Date.now();
    const newMessage = {
      id: appMsgId,
      content: messageContent,
      type: "text",
      time: currentTime,
      isSelf: true,
      status: "sending",
      showTime: shouldShowTime(
        currentTime,
        messages[messages.length - 1]?.time || 0
      ),
      timeStr: formatTime(currentTime),
    };
    setMessages((msgs) => [...msgs, newMessage]);
    setInputMessage("");
    setLastMessageTime(Math.max(lastMessageTime, currentTime));
    try {
      const res = await pushDriverMsg({
        appMsgId,
        msgContent: JSON.stringify({ text: messageContent }),
        msgType: 1,
        orderId,
      });
      if (res.data.errorCode == 0) {
        setMessages((msgs) =>
          msgs.map((msg) =>
            msg.id === appMsgId
              ? { ...msg, status: "sent" }
              : msg
          )
        );
        // 不再设置 msgId
        // setMessageIdMap((map) => ({ ...map, [appMsgId]: serverMsgId }));
      } else {
        setMessages((msgs) =>
          msgs.map((msg) =>
            msg.id === appMsgId ? { ...msg, status: "failed" } : msg
          )
        );
        alert(res.message || t('chat.sendFailed'));
      }
    } catch {
      setMessages((msgs) =>
        msgs.map((msg) =>
          msg.id === appMsgId ? { ...msg, status: "failed" } : msg
        )
      );
      alert(t('chat.networkError'));
    }
  };

  // 选择图片
  const chooseImage = async (e) => {
    if (!canChat) return; // 禁止发送
    const file = e.target.files[0];
    if (!file) return;
    const appMsgId = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const currentTime = Date.now();
    const localUrl = URL.createObjectURL(file);
    const newMessage = {
      id: appMsgId,
      content: localUrl,
      type: "image",
      time: currentTime,
      isSelf: true,
      status: "sending",
      showTime: shouldShowTime(currentTime, messages[messages.length - 1]?.time || 0),
      timeStr: formatTime(currentTime),
      appMsgId,
    };
    setMessages((msgs) => [...msgs, newMessage]);
    setLastMessageTime(Math.max(lastMessageTime, currentTime));
    try {
      // 使用 uploadFile 方法上传图片
      const uploadResult = await uploadFile(file, "UPLOAD_DRIVETR_EXAM_IMG");
      console.log("上传结果:", uploadResult);
      if(uploadResult.data.errorCode != 0) {
        throw new Error(uploadResult.message || "上传失败");
      }
      const imageUrl = uploadResult.data?.data?.url;
      setMessages((msgs) =>
        msgs.map((msg) =>
          msg.id === appMsgId ? { ...msg, content: imageUrl } : msg
        )
      );
      const res = await pushDriverMsg({
        appMsgId,
        msgContent: JSON.stringify({ url: imageUrl }),
        msgType: 4,
        orderId,
      });
      if (res.data.errorCode == 0) {
        setMessages((msgs) =>
          msgs.map((msg) =>
            msg.id === appMsgId ? { ...msg, status: "sent" } : msg
          )
        );
        // 不再设置 msgId
        // setMessageIdMap((map) => ({ ...map, [appMsgId]: serverMsgId }));
      } else {
        setMessages((msgs) =>
          msgs.map((msg) =>
            msg.id === appMsgId ? { ...msg, status: "failed" } : msg
          )
        );
        alert(res.message || t('chat.sendFailed'));
      }
    } catch (error) {
      setMessages((msgs) =>
        msgs.map((msg) =>
          msg.id === appMsgId ? { ...msg, status: "failed" } : msg
        )
      );
      alert(error.message || t('chat.uploadFailed'));
    }
  };

  // 预览图片
  const previewImage = (src) => {
    setPreviewSrc(src);
    setPreviewVisible(true);
  };

  // 返回上一页
  const goBack = () => window.history.back();

  return (
    <div className="chat-container">
      {/* 顶部导航栏 */}
      {showTop && (
        <div className="top" >
           <span
           className="close-icon"
            onClick={() => setShowTop(false)}
            title={t('chat.close')}
          >
            ×
          </span>
          {t('chat.title', { passengerPhoneSuffix })}
        </div>
      )}
      {/* 消息区域 */}
      <div
        className="message-list"
        ref={messageListRef} // 新增ref
        onScroll={(e) => {
          if (e.target.scrollTop === 0) loadMoreMessages();
        }}
      >
        {isLoadingMore && (
          <div className="loading">
            <span>{t('chat.loadingMore')}</span>
          </div>
        )}
        {messages.map((item, idx) => (
          <React.Fragment key={item.id}>
            {item.showTime && <div className="timestamp">{item.timeStr}</div>}
            <div
              className={`message-item ${item.isSelf ? "self" : "other"}`}
              id={`msg-${item.id}`}
            >
              {/* 头像 */}
              {/* {!item.isSelf && (
                <div className="avatar">
                  <img
                    src={
                      "https://sxz-tsp-public-sz.oss-cn-shenzhen.aliyuncs.com/fly/tx3.png"
                    }
                    alt="avatar"
                  />
                </div>
              )} */}
              {/* 消息内容 */}
              <div
                className={`message-content ${
                  item.isSelf ? "self-content" : "other-content"
                }`}
              >
                {item.type === "text" && <span>{item.content}</span>}
                {item.type === "image" && (
                  <img
                    src={item.content}
                    alt="img"
                    style={{ maxWidth: "100%" }}
                    onClick={() => previewImage(item.content)}
                  />
                )}
                {/* 位置消息可按需实现 */}
              </div>
              {/* 消息状态 */}
              {item.isSelf && (
                <div className="message-status">
                   {item.readStatus === 0 && <span>{t('chat.unread')}</span>}
                  {item.status === "sending" && <span>{t('chat.sending')}</span>}
                  {item.status === "failed" && (
                    <span style={{ color: "#ff4d4f" }}>{t('chat.failed')}</span>
                  )}
                </div>
              )}
            </div>
          </React.Fragment>
        ))}
        <div className="scroll-bottom" ref={scrollBottomRef}></div>
      </div>
      {/* 输入区域 */}
      <div className="input-area">
        {!canChat ? (
          <div className="chat-ended-tip" style={{ color: "#999", textAlign: "center", padding: "16px 0" }}>
            {t('chat.endedTip')}
          </div>
        ) : (
          <>
            <div className="input-toolbar">
              <label>
                <span style={{ fontSize: 28, color: "#9ca3af", cursor: "pointer" }}>
                  📷
                </span>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={chooseImage}
                />
              </label>
            </div>
            <div className="input-box">
              <input
                className="message-input"
                value={inputMessage}
                onChange={onInputChange}
                placeholder={t('chat.inputPlaceholder')}
                onKeyDown={(e) => {
                  if (e.key === "Enter") sendMessage();
                }}
                disabled={!canChat}
              />
              <div
                className={`send-btn ${inputMessage ? "active" : ""}`}
                onClick={sendMessage}
              >
                {t('chat.send')}
              </div>
            </div>
          </>
        )}
      </div>
      {/* 图片预览弹窗 */}
      {previewVisible && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.8)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setPreviewVisible(false)}
        >
          <img
            src={previewSrc}
            alt="预览"
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
              borderRadius: 8,
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default ChatPage;
